import { Address, isAddress, makeAddressSync, ServerError } from "@silo/types";
import { Effect } from "effect";
import { KvClientError, KvService } from "../kv-client/types";
import { setSanctionedAddresses } from "../kv-client/sanctioned-addresses";
import { SAXParser } from "sax";

export function updateSanctionedAddresses(): Effect.Effect<
  void,
  ServerError | KvClientError,
  KvService
> {
  return Effect.gen(function* () {
    yield* Effect.log(`Begin 🎬: update sanctioned addresses`);

    const { addresses, total, unique } = yield* Effect.tryPromise({
      try: async () => {
        return getAddresses();
      },
      catch: (cause) => new ServerError({ cause }),
    });

    yield* Effect.log(`Total sanctioned addresses: ${total}`);
    yield* Effect.log(`Unique sanctioned addresses: ${unique}`);

    yield* setSanctionedAddresses(addresses);

    yield* Effect.log(`Complete ✅: update sanctioned addresses`);
  });
}

interface GetAddressesReturnType {
  addresses: Address[];
  total: number;
  unique: number;
}

const SDN_URL =
  "https://sanctionslistservice.ofac.treas.gov/api/PublicationPreview/exports/SDN.XML";

async function getAddresses(): Promise<GetAddressesReturnType> {
  const addresses: Address[] = [];
  const parser = new SAXParser(true);

  const response = await fetch(SDN_URL);

  // only process <idNumber> tags
  let isIdNumber = false;

  parser.onopentag = (node) => {
    if (isIdNumberTag(node.name)) {
      isIdNumber = true;
    }
  };

  parser.onclosetag = (tag) => {
    if (isIdNumberTag(tag)) {
      isIdNumber = false;
    }
  };

  parser.ontext = (value) => {
    if (isIdNumber && isAddress(value)) {
      addresses.push(makeAddressSync(value));
    }
  };

  const reader = response.body?.getReader();

  if (!reader) {
    throw new Error("SDN response does not have a body");
  }

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();

    if (done) {
      break;
    }

    const chunk = Buffer.from(value).toString("utf-8");
    parser.write(chunk);
  }

  parser.end();

  const set = new Set(addresses);

  return {
    addresses: [...set],
    total: addresses.length,
    unique: set.size,
  };
}

function isIdNumberTag(tag: string): boolean {
  return tag.toLowerCase() === "idnumber";
}
